import api from '@/global/api';
import { getTimeRange, DAY, SHARED_TIME_RANGES } from '@/processes/Settings';
import qs from 'query-string';
import _get from 'lodash/get';
import _sortBy from 'lodash/sortBy';
import _sumBy from 'lodash/sumBy';
import _toLower from 'lodash/toLower';
import { getFloatOrDecimalNumber } from '@/shared/utils/formatters';
import { getApiMiningType } from '@/shared/utils/apiMiningType';

export const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getWorkersCount: build.query({
      query: (params = {}) => {
        const {
          dateRange = DAY,
          account, 
          miner,
          coin, 
          worker = '',
          offset = 0
        } = params;
        const { window, limit } = getTimeRange('', dateRange);
        const queryParams = qs.stringify({
          coin,
          worker,
          window,
          limit,
          offset,
        }, { skipNull: true })

        const { urlPart } = getApiMiningType({ miner, account, coin });

        return {
          url: `/v1/${urlPart}/workers-count?${queryParams}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const items = _get(response, 'items', []);
        return _sortBy(items, 'timestamp').map((item: any) => (
          {
            ...item,
            ...{
              count: getFloatOrDecimalNumber(item.count),
            }
          }
        ));
      }
    }),
    getMinerWorkers: build.query({
      query: (params = {}) => {
        const {
          dateRange = DAY,
          account, 
          miner,
          coin, 
          worker = '',
          offset = 0
        } = params;
        const { window, limit } = getTimeRange('', dateRange);
        const queryParams = qs.stringify({
          coin,
          worker,
          window,
          limit,
          offset,
        }, { skipNull: true })

        const { urlPart } = getApiMiningType({ miner, account, coin });

        return {
          url: `/v1/${urlPart}/miner/workers?${queryParams}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const workersObject = _get(response, 'workers', {}) || {};
        const workers = Object.entries(workersObject).map((item: any) => {
          const [workerName, workerStat] = item;
          const {
            online = false,
            hashrate = 0,
            hashrate24h = 0,
            sharesStatusStats = {},
          } = workerStat;
          const {
            lastShareDt,
            staleCount,
            staleRate,
            validCount,
            validRate,
            invalidRate,
            invalidCount,
          } = sharesStatusStats;

          return {
            workerName,
            online: online || false,
            hashrate: Number(hashrate) || 0,
            hashrate24h: Number(hashrate24h) || 0,
            staleCount: Number(staleCount) || 0,
            staleRate: Number(staleRate) || 0,
            validCount: Number(validCount) || 0,
            validRate: Number(validRate) || 0,
            invalidRate: Number(invalidRate) || 0,
            invalidCount: Number(invalidCount) || 0,
            lastShareDt: lastShareDt,
          }
        });
        
        const onlineWorkersCount = workers.filter((worker) => worker.online).length;
        const offlineWorkersCount = workers.filter((worker) => !worker.online).length;

        const validSharesTotal = _sumBy(workers, 'validCount');
        const invalidSharesTotal = _sumBy(workers, 'invalidCount');
        const staleSharesTotal = _sumBy(workers, 'staleCount');
        const allSharesCount = validSharesTotal + invalidSharesTotal + staleSharesTotal;
        const validRateTotal = getFloatOrDecimalNumber(validSharesTotal / allSharesCount * 100);
        const invalidRateTotal = getFloatOrDecimalNumber(invalidSharesTotal / allSharesCount * 100);
        const staleRateTotal = getFloatOrDecimalNumber(staleSharesTotal / allSharesCount * 100);

        return {
          workers,
          onlineWorkersCount,
          offlineWorkersCount,
          validSharesTotal,
          invalidSharesTotal,
          staleSharesTotal,
          validRateTotal,
          invalidRateTotal,
          staleRateTotal,
        }
      }
    }),
    getWorkersCountShared: build.query({
      query: (params = {}) => {
        const {
          dateRange = DAY,
          miner: minerAddress,
          coin,
          worker = '',
          offset = 0
        } = params;
        const { window, limit } = _get(SHARED_TIME_RANGES, dateRange);
        const queryParams = qs.stringify({
          minerAddress,
          coin,
          worker,
          window,
          limit,
          offset,
        }, { skipNull: true })

        return {
          url: `/v1/stats/workers-count?${queryParams}`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const items = _get(response, 'items', []);
        return _sortBy(items, 'timestamp').map((item: any) => (
          {
            ...item,
            ...{
              count: getFloatOrDecimalNumber(item.count),
            }
          }
        ));
      }
    }),
    getMinerWorkersShared: build.query({
      query: (params = {}) => {
        const {
          miner: minerAddress,
          coin,
        } = params;

        return {
          url: `/v1/stats/miner/${minerAddress}/${coin}/workers`,
          method: 'GET',
        }
      },
      transformResponse: (response: any) => {
        const workersObject = _get(response, 'workers', {}) || {};
        return Object.entries(workersObject).map((item: any) => {
          const [workerName, workerStat] = item;
          const {
            online = false,
            hashrate = 0,
            hashrate24h = 0,
            sharesStatusStats = {},
          } = workerStat;
          const {
            lastShareDt,
            staleCount,
            staleRate,
            validCount,
            validRate,
            invalidRate,
            invalidCount,
          } = sharesStatusStats;

          return {
            workerName,
            online: online || false,
            hashrate: Number(hashrate) || 0,
            hashrate24h: Number(hashrate24h) || 0,
            staleCount: Number(staleCount) || 0,
            staleRate: Number(staleRate) || 0,
            validCount: Number(validCount) || 0,
            validRate: Number(validRate) || 0,
            invalidRate: Number(invalidRate) || 0,
            invalidCount: Number(invalidCount) || 0,
            lastShareDt: lastShareDt,
          }
        });
      }
    }),
  }),
});

export const {
  useGetWorkersCountQuery,
  useGetMinerWorkersQuery,
  useGetWorkersCountSharedQuery,
  useGetMinerWorkersSharedQuery,
} = extendedApi;

export const {
  getWorkersCount,
  getMinerWorkers,
  getWorkersCountShared,
  getMinerWorkersShared,
} = extendedApi.endpoints;
